import React from "react";
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Table, Form, Button, Alert, Badge } from "react-bootstrap";
import { AuthenticationService } from "../../services/auth.service";
import { Toggle, Field, Label } from "@zendeskgarden/react-forms";
import UserService from "../../services/user.service";
import { ThemeProvider } from "@zendeskgarden/react-theming";
import { theme } from '../../util/colorButton'
import { AlertService } from "../../services/alert.service";
import "./index.css";

// Tipagem de usuário
interface User {
  id: string;
  name: string;
  email: string;
  status: "ativo" | "inativo" | { nome?: string; descricao?: string };
  roles: string[];
  created: string;
  updated: string;
  updatedBy: {
    id: string;
    displayName?: string;
  };
  displayName?: string;
  contato?: {
    displayName?: string;
    lista?: any[];
  };
  validRegistration?: boolean;
}

const AVAILABLE_ROLES = ["user", "recruitment", "rh", "rh_manager"];

interface ProfileManagementPageState {
  users: User[];
  search: string;
  statusFilter: string;
  roleFilter: string;
  alert: string | null;
  currentPage: number;
  totalPages: number;
  usersPerPage: number;
  editingUserId: string | null;
  editingRoles: string[];
  filteredTotal: number;
  loading: boolean;
}

class ProfileManagementPage extends React.Component<RouteComponentProps, ProfileManagementPageState> {
  currentUser = AuthenticationService.getUser();
  debounceTimer: NodeJS.Timeout | null = null;
  redirectToHome: () => void = () => {
    this.props.history.push("/");
  };
  
  constructor(props: any) {
    super(props);
    this.state = {
      users: [] as User[],
      search: "",
      statusFilter: "ativo",
      roleFilter: "rh_manager",
      alert: null as string | null,
      currentPage: 1,
      totalPages: 1,
      usersPerPage: 20,
      editingUserId: null as string | null,
      editingRoles: [] as string[],
      filteredTotal: 0,
      loading: false,
    };
  }

  componentDidMount() {
    const isRhManager = this.currentUser.roles.includes("rh_manager");

    if (!isRhManager) {
      AuthenticationService.logout();
      this.redirectToHome();
    }

    this.loadAllUsers();
  }

  componentDidUpdate(_prevProps: any, prevState: any) {
    const { currentPage, statusFilter, roleFilter } = this.state as any;
    if (
      prevState.currentPage !== currentPage ||
      prevState.statusFilter !== statusFilter ||
      prevState.roleFilter !== roleFilter
    ) {
      this.loadAllUsers();
    }
  }

  loadAllUsers = async () => {
    this.setState({ loading: true });
    let validRegistrationFilter: boolean | undefined = false;
    const { currentPage, usersPerPage, search, statusFilter, roleFilter } = this.state as any;

    if (statusFilter === "ativo") {
      validRegistrationFilter = true;
    }

    try {
      const response = await UserService.getAllUsersForRhManager(
        currentPage,
        usersPerPage,
        search,
        statusFilter,
        [roleFilter],
        validRegistrationFilter
      );
      const list = response.data?.usersList || [];
      const totalUsersFound = response.data?.total;
      this.setState({
        users: list,
        totalPages: Math.max(1, Math.ceil(totalUsersFound / usersPerPage)),
        filteredTotal: totalUsersFound,
        loading: false,
      });
    } catch (error) {
      this.setState({ loading: false });
      console.error("Erro ao determinar filtro de status:", error);
    }
  };

  startEditRoles = (user: User) => {
    this.setState({
      editingUserId: user.id,
      editingRoles: [...user.roles],
    });
  };

  cancelEditRoles = () => {
    this.setState({
      editingUserId: null,
      editingRoles: [],
    });
  };

  saveEditRoles = async (user: User) => {
    const { editingRoles } = this.state as any;
    try {
      if (!editingRoles.includes("user")) {
        this.setState({ alert: "Todo usuário deve possuir ao menos o perfil 'user'" });
        return;
      }
      if (
        user.id === this.currentUser._id &&
        user.roles.includes("rh_manager") &&
        !editingRoles.includes("rh_manager")
      ) {
        this.setState({ alert: "Você não pode remover seu próprio perfil de gerente de RH" });
        return;
      }
      await UserService.updateUserRhManager(user.id, editingRoles);
      this.setState({
        editingUserId: null,
        editingRoles: [],
      });
      AlertService.success(`O Perfil do usuário ${user.displayName} foi atualizado com sucesso`);
      this.loadAllUsers();
    } catch (err) {
      this.setState({ alert: "Erro ao atualizar perfil" });
    }
  };

  handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    this.setState({ search: value, currentPage: 1 });

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.loadAllUsers();
    }, 1000);
  };

  handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState({ statusFilter: e.target.value, currentPage: 1 });
  };

  handleRoleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState({ roleFilter: e.target.value, currentPage: 1 });
  };

  handlePrevPage = () => {
    this.setState((prevState: any) => ({
      currentPage: Math.max(1, prevState.currentPage - 1),
    }));
  };

  handleNextPage = () => {
    this.setState((prevState: any) => ({
      currentPage: Math.min(prevState.totalPages, prevState.currentPage + 1),
    }));
  };

  handleRoleCheckboxChange = (role: string, checked: boolean) => {
    this.setState((prevState: any) => {
      let editingRoles = [...prevState.editingRoles];

      if (role === "rh_manager" && checked && !prevState.editingRoles.includes("rh")) {
        AlertService.warn("O perfil 'rh_manager' requer o perfil 'rh'.");
        return { editingRoles };
      }

      if (role === "rh" && !checked && editingRoles.includes("rh_manager")) {
        editingRoles = editingRoles.filter(roleEdit => roleEdit !== "rh_manager" && roleEdit !== "rh");
        return { editingRoles };
      }
      
      if (checked) {
        editingRoles.push(role);
      } else {
        editingRoles = editingRoles.filter((r: string) => r !== role);
      }

      return { editingRoles };
    });
  };

  toggleUserStatus = (selectedUser: User, isChecked: boolean) => {
    this.setState((prevState: any) => {
      const updatedUsers = prevState.users.map((currentUser: User) => {
        if (currentUser.id === selectedUser.id) {
          return { ...currentUser, validRegistration: isChecked };
        }
        return currentUser;
      });
      return { users: updatedUsers };
    }, async () => {
      try {
        await UserService.setStatusUser(selectedUser.id, isChecked, this.currentUser._id);
        AlertService.success(`O Perfil do usuário ${selectedUser.displayName} foi atualizado com sucesso`);
        this.loadAllUsers();
      } catch (error) {
        AlertService.error(`Erro ao atualizar o perfil do usuário ${selectedUser.displayName}`)
        console.error("Erro ao atualizar status do usuário:", error);
      }
    });
  };

  render() {
    const {
      users,
      search,
      statusFilter,
      roleFilter,
      alert,
      currentPage,
      totalPages,
      editingUserId,
      editingRoles,
    } = this.state as any;

    return (
      <div className="container mt-4">
        <div className="profile-management-container">
          <div className="profile-management-header">
            <h1 className="profile-management-title">Gestão de Perfis</h1>
            <p className="profile-management-subtitle">Área para gerenciar perfis de usuários.</p>
            <Form className="d-flex align-items-center mb-3 gap-3 profile-management-form">
              <Form.Group controlId="search" className="mb-0 me-3 flex-grow-1 profile-management-form-group">
                <Form.Control
                  type="text"
                  placeholder="Buscar por nome ou e-mail..."
                  value={search}
                  onChange={this.handleSearchChange}
                  className="profile-management-form-control"
                />
              </Form.Group>
              <div className="d-flex align-items-center gap-2" style={{ marginLeft: 50 }}>
                <label htmlFor="statusFilter" className="profile-management-status-label">Status: </label>
                <Form.Group controlId="statusFilter" className="mb-0">
                  <Form.Control
                    as="select"
                    value={statusFilter}
                    onChange={this.handleStatusFilterChange}
                    className="profile-management-select"
                  >
                    <option value="ativo">Ativo</option>
                    <option value="inativo">Inativo</option>
                  </Form.Control>
                </Form.Group>
              </div>
              <div className="d-flex align-items-center gap-2" style={{ marginLeft: 50 }}>
                <label htmlFor="roleFilter" className="profile-management-role-label">Perfil: </label>
                <Form.Group controlId="roleFilter" className="mb-0">
                  <Form.Control
                    as="select"
                    value={roleFilter}
                    onChange={this.handleRoleFilterChange}
                    className="profile-management-select"
                  >
                    {AVAILABLE_ROLES.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
                {/* Removido o botão de atualizar */}
              </div>
            </Form>
          </div>

          {alert && <Alert variant="danger">{alert}</Alert>}

          {/* Loading centralizado acima da tabela */}
          {this.state.loading && (
            <div className="profile-management-loading">
              Carregando...
            </div>
          )}

          {/* Página/total centralizado acima da tabela */}
          <div className="profile-management-pagination">
            <span>
              Página {currentPage} de {totalPages}
                {" — "}
                {(() => {
                let totalLabel = "usuários";
                if (statusFilter === "ativo") totalLabel = "ativos";
                else if (statusFilter === "inativo") totalLabel = "inativos";
                return `Total ${totalLabel}: ${this.state.filteredTotal}`;
                })()}
            </span>
          </div>

          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Email</th>
                <th>Status</th>
                <th>Perfis</th>
                <th>Data de criação</th>
                <th>Última atualização</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user: User) => (
                <tr key={user.id}>
                  <td>{user.displayName || user.name || user.contato?.displayName}</td>
                  <td>{user.email}</td>
                  <td style={{width: '120px', textAlign: 'center'}}>
                    <div className="mb-4">
                      {user.validRegistration
                        ? <Badge variant="success">Ativo</Badge>
                        : <Badge variant="secondary">Inativo</Badge>
                      }
                    </div>
                    <div className="ml-2">
                      <ThemeProvider theme={theme as any}>
                        <Field>
                          <Toggle
                            checked={user.validRegistration}
                            onChange={(event) => {
                              this.toggleUserStatus(user, event.target.checked);
                            }}
                          >
                            <Label>
                            </Label>
                          </Toggle>
                        </Field>
                      </ThemeProvider>
                    </div>
                  </td>
                  <td className="d-flex justify-content-between align-items-center">
                    <div>
                      {editingUserId === user.id ? (
                        <>
                          {AVAILABLE_ROLES.map(role => (
                            <Form.Check
                              key={role}
                              type="checkbox"
                              label={role}
                              checked={editingRoles.includes(role)}
                              disabled={
                                (role === "user") ||
                                (role === "rh_manager" && user.id === this.currentUser._id)
                              }
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                if (role === "user") return;
                                if (role === "rh_manager" && user.id === this.currentUser._id) return;
                                this.handleRoleCheckboxChange(role, e.target.checked);
                              }}
                            />
                          ))}
                        </>
                      ) : (
                        <>
                          {AVAILABLE_ROLES.map(role => (
                            <Form.Check
                              key={role}
                              type="checkbox"
                              label={role}
                              checked={user.roles.includes(role)}
                              disabled
                            />
                          ))}
                        </>
                      )}
                    </div>
                    <div className="h-100" style={{ minHeight: '120px' }}>
                      {editingUserId === user.id ? (
                        <div className="h-100 w-100 d-flex flex-column justify-content-around">
                          <Button
                            style={{ marginBottom: '5px'}}
                            variant="success"
                            size="sm"
                            onClick={() => this.saveEditRoles(user)}
                          >
                            Salvar
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={this.cancelEditRoles}
                          >
                            Cancelar
                          </Button>
                        </div>
                      ) : (
                        <Button variant="outline-primary" size="sm" onClick={() => this.startEditRoles(user)}>
                          Editar
                        </Button>
                      )}
                    </div>
                  </td>
                  <td>{new Date(user.created).toLocaleDateString()}</td>
                  <td>
                    <p>
                      {new Date(user.updated).toLocaleString()}
                    </p>
                    <p>
                      {`Atualizado por (${user.updatedBy?.displayName || 'Sem informação'})`}
                    </p>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* Navegação de páginas abaixo da tabela */}
          <div className="profile-management-table-pagination">
            <Button
              disabled={currentPage === 1}
              onClick={this.handlePrevPage}
              className="button-pagination"
            >
              Anterior
            </Button>
            <span>
              Página {currentPage} de {totalPages}
                {" — "}
                {(() => {
                let totalLabel = "usuários";
                if (statusFilter === "ativo") totalLabel = "ativos";
                else if (statusFilter === "inativo") totalLabel = "inativos";
                return `Total ${totalLabel}: ${this.state.filteredTotal}`;
                })()}
            </span>
            <Button
              disabled={currentPage === totalPages}
              onClick={this.handleNextPage}
              className="button-pagination"
            >
              Próxima
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(ProfileManagementPage);
